/**
 * Epic 10 — Knowledge Base Panel (admin/SME only).
 *
 * Lists doctrine documents stored in the backend, provides:
 *  - Upload (POST /api/documents with plain-text/markdown body)
 *  - Ingest (POST /api/documents/{id}/ingest — chunk + embed)
 *  - Extract (POST /api/documents/{id}/extract — AI proposes DoctrineRequirements)
 *  - Delete (DELETE /api/documents/{id})
 *  - Approve proposal (PATCH /api/requirements/{id} with status: 'approved')
 *
 * When storage is not configured the panel shows an advisory and hides actions.
 * All actions are advisory — they never replace the deterministic rule engine.
 */

import { useEffect, useState, useCallback } from 'react';
import {
  Upload,
  Zap,
  Trash2,
  Sparkles,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  FileText,
} from 'lucide-react';
import type { DoctrineDocument, ExtractionProposal } from '@duty/shared';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DocumentWithProposals extends DoctrineDocument {
  proposals?: ExtractionProposal[];
}

const ingestionStatusIcon = (s?: string) => {
  if (s === 'ingested') return <CheckCircle2 size={12} className="text-emerald-500" aria-label="Ingested" />;
  if (s === 'ingesting') return <Clock size={12} className="text-amber-500 animate-pulse" aria-label="Ingesting" />;
  if (s === 'failed') return <AlertTriangle size={12} className="text-red-500" aria-label="Failed" />;
  return <Clock size={12} className="text-muted-foreground" aria-label="Pending" />;
};

// ── Upload form state ─────────────────────────────────────────────────────────

interface UploadForm {
  title: string;
  type: string;
  version: string;
  body: string;
  states: string;
  incidentTypes: string;
}

const EMPTY_FORM: UploadForm = {
  title: '',
  type: 'SOP',
  version: '1.0',
  body: '',
  states: 'NSW',
  incidentTypes: 'StructureFire',
};

// ── Panel ─────────────────────────────────────────────────────────────────────

export function KnowledgeBasePanel() {
  const [docs, setDocs] = useState<DocumentWithProposals[]>([]);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState<UploadForm>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const loadDocs = useCallback(async () => {
    try {
      const res = await fetch('/api/documents');
      if (!res.ok) return;
      const data = (await res.json()) as { documents: DoctrineDocument[]; configured: boolean };
      setDocs(data.documents);
      setConfigured(data.configured);
    } catch {
      setConfigured(false);
    }
  }, []);

  useEffect(() => { void loadDocs(); }, [loadDocs]);

  const toggleExpanded = (id: string) =>
    setExpanded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const setBusyFor = (id: string, v: boolean) => setBusy((p) => ({ ...p, [id]: v }));

  const handleIngest = async (docId: string) => {
    setBusyFor(docId, true);
    setError(null);
    try {
      const res = await fetch(`/api/documents/${docId}/ingest`, { method: 'POST' });
      if (!res.ok) {
        const d = (await res.json()) as { error?: string };
        setError(d.error ?? 'Ingest failed');
      }
    } catch {
      setError('Network error during ingest');
    } finally {
      setBusyFor(docId, false);
      await loadDocs();
    }
  };

  const handleExtract = async (docId: string) => {
    setBusyFor(`extract-${docId}`, true);
    setError(null);
    try {
      const res = await fetch(`/api/documents/${docId}/extract`, { method: 'POST' });
      if (!res.ok) {
        const d = (await res.json()) as { error?: string };
        setError(d.error ?? 'Extraction failed');
        return;
      }
      const data = (await res.json()) as { proposals: ExtractionProposal[]; aiUsed: boolean; advisory?: string };
      setDocs((prev) =>
        prev.map((d) => (d.id === docId ? { ...d, proposals: data.proposals } : d)),
      );
      if (data.proposals.length === 0) {
        setError(data.advisory ?? 'No proposals extracted.');
      }
    } catch {
      setError('Network error during extraction');
    } finally {
      setBusyFor(`extract-${docId}`, false);
    }
  };

  const handleDelete = async (docId: string) => {
    if (!confirm('Delete this document and all its chunks?')) return;
    setBusyFor(`del-${docId}`, true);
    try {
      await fetch(`/api/documents/${docId}`, { method: 'DELETE' });
    } finally {
      setBusyFor(`del-${docId}`, false);
      await loadDocs();
    }
  };

  const handleApprove = async (reqId: string, docId: string) => {
    setBusyFor(`approve-${reqId}`, true);
    try {
      const res = await fetch(`/api/requirements/${reqId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'approved' }),
      });
      if (res.ok) {
        // Remove the proposal from the list (it's now in the requirements engine).
        setDocs((prev) =>
          prev.map((d) =>
            d.id === docId
              ? { ...d, proposals: (d.proposals ?? []).filter((p) => p.id !== reqId) }
              : d,
          ),
        );
      }
    } finally {
      setBusyFor(`approve-${reqId}`, false);
    }
  };

  const handleUpload = async () => {
    if (!form.title || !form.body) {
      setError('Title and document body are required');
      return;
    }
    setError(null);
    setBusy((p) => ({ ...p, upload: true }));
    try {
      const res = await fetch('/api/documents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title,
          type: form.type,
          version: form.version,
          body: form.body,
          applicability: {
            states: form.states.split(',').map((s) => s.trim()).filter(Boolean),
            incidentTypes: form.incidentTypes.split(',').map((s) => s.trim()).filter(Boolean),
          },
        }),
      });
      if (!res.ok) {
        const d = (await res.json()) as { error?: string };
        setError(d.error ?? 'Upload failed');
        return;
      }
      setForm(EMPTY_FORM);
      setShowUpload(false);
      await loadDocs();
    } catch {
      setError('Network error during upload');
    } finally {
      setBusy((p) => ({ ...p, upload: false }));
    }
  };

  if (configured === false) {
    return (
      <p className="text-xs text-muted-foreground italic px-1">
        Knowledge Base requires Azure Table Storage. Configure <code>AZURE_STORAGE_CONNECTION_STRING</code> to enable document management.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded px-2 py-1.5 flex gap-1.5 items-start">
          <AlertTriangle size={12} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{docs.length} document{docs.length !== 1 ? 's' : ''}</span>
        <button
          onClick={() => { setShowUpload((v) => !v); setError(null); }}
          className="text-xs flex items-center gap-1 text-primary hover:underline"
        >
          <Upload size={11} />
          {showUpload ? 'Cancel' : 'Add document'}
        </button>
      </div>

      {showUpload && (
        <div className="border border-border rounded p-3 space-y-2 bg-muted/20">
          <p className="text-xs font-medium">Add doctrine document</p>
          {(['title', 'type', 'version'] as const).map((f) => (
            <div key={f}>
              <label className="text-xs text-muted-foreground capitalize">{f}</label>
              <input
                className="w-full text-xs border border-border rounded px-2 py-1 bg-background mt-0.5"
                value={form[f]}
                onChange={(e) => setForm((p) => ({ ...p, [f]: e.target.value }))}
                placeholder={f === 'title' ? 'NSW RFS Structure Fire SOP v2.1' : f === 'type' ? 'SOP' : '1.0'}
              />
            </div>
          ))}
          <div>
            <label className="text-xs text-muted-foreground">States (comma-separated)</label>
            <input
              className="w-full text-xs border border-border rounded px-2 py-1 bg-background mt-0.5"
              value={form.states}
              onChange={(e) => setForm((p) => ({ ...p, states: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Incident types (comma-separated)</label>
            <input
              className="w-full text-xs border border-border rounded px-2 py-1 bg-background mt-0.5"
              value={form.incidentTypes}
              onChange={(e) => setForm((p) => ({ ...p, incidentTypes: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Document body (plain text / markdown)</label>
            <textarea
              className="w-full text-xs border border-border rounded px-2 py-1 bg-background mt-0.5 h-28 resize-y font-mono"
              value={form.body}
              onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
              placeholder="Paste the document text here…"
            />
          </div>
          <button
            onClick={handleUpload}
            disabled={busy.upload}
            className="text-xs bg-primary text-primary-foreground px-3 py-1 rounded disabled:opacity-50"
          >
            {busy.upload ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      )}

      {docs.length === 0 && !showUpload && (
        <p className="text-xs text-muted-foreground italic">No documents stored. Add a document to enable AI-assisted requirement extraction.</p>
      )}

      <div className="space-y-2">
        {docs.map((doc) => {
          const isExpanded = expanded.has(doc.id);
          return (
            <div key={doc.id} className="border border-border rounded overflow-hidden">
              <button
                onClick={() => toggleExpanded(doc.id)}
                className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-muted/30 transition-colors"
                aria-expanded={isExpanded}
              >
                <span className="mt-0.5 shrink-0 text-muted-foreground">
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
                <FileText size={13} className="shrink-0 mt-0.5 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium truncate">{doc.title}</span>
                    <span className="text-xs text-muted-foreground">v{doc.version}</span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      {ingestionStatusIcon(doc.ingestionStatus)}
                      {doc.ingestionStatus ?? 'pending'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{doc.type} — {doc.applicability.states?.join(', ')} / {doc.applicability.incidentTypes?.join(', ')}</p>
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-border px-3 py-2 space-y-3">
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => handleIngest(doc.id)}
                      disabled={!!busy[doc.id]}
                      className="text-xs flex items-center gap-1 bg-muted px-2 py-1 rounded hover:bg-muted/70 disabled:opacity-50"
                    >
                      <Zap size={11} />
                      {busy[doc.id] ? 'Ingesting…' : 'Ingest / re-embed'}
                    </button>
                    <button
                      onClick={() => handleExtract(doc.id)}
                      disabled={!!busy[`extract-${doc.id}`]}
                      className="text-xs flex items-center gap-1 bg-muted px-2 py-1 rounded hover:bg-muted/70 disabled:opacity-50"
                    >
                      <Sparkles size={11} />
                      {busy[`extract-${doc.id}`] ? 'Extracting…' : 'AI extract requirements'}
                    </button>
                    <button
                      onClick={() => handleDelete(doc.id)}
                      disabled={!!busy[`del-${doc.id}`]}
                      className="text-xs flex items-center gap-1 text-destructive/70 hover:text-destructive px-2 py-1 rounded disabled:opacity-50 ml-auto"
                    >
                      <Trash2 size={11} />
                      Delete
                    </button>
                  </div>

                  {doc.ingestionStatus === 'ingested' && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">
                      Embedded — Ask Duty will use cosine retrieval for this document when AI is available.
                    </p>
                  )}

                  {(doc.proposals ?? []).length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-amber-700 dark:text-amber-400 flex items-center gap-1">
                        <Sparkles size={11} />
                        AI proposals — review before approving
                      </p>
                      {(doc.proposals ?? []).map((p) => (
                        <div key={p.id} className="border border-amber-200 dark:border-amber-800 rounded p-2 space-y-1 bg-amber-50/50 dark:bg-amber-950/20">
                          <div className="flex items-start justify-between gap-2">
                            <div className="text-xs space-y-0.5">
                              <span className="font-mono font-medium">{p.parameter.key}</span>
                              <span className="text-muted-foreground ml-2">
                                {p.parameter.operator === 'min' ? '≥' : p.parameter.operator === 'max' ? '≤' : '='}{' '}
                                {p.parameter.value}{p.parameter.unit ? ` ${p.parameter.unit}` : ''}
                              </span>
                              <p className="text-muted-foreground text-xs mt-0.5 italic">"{p.verbatimQuote}"</p>
                              <p className="text-xs text-muted-foreground">AI confidence: {Math.round(p.aiConfidence * 100)}%</p>
                            </div>
                            <button
                              onClick={() => handleApprove(p.id, doc.id)}
                              disabled={!!busy[`approve-${p.id}`]}
                              className="text-xs shrink-0 flex items-center gap-1 bg-emerald-600 text-white px-2 py-0.5 rounded hover:bg-emerald-700 disabled:opacity-50"
                            >
                              <CheckCircle2 size={10} />
                              {busy[`approve-${p.id}`] ? 'Approving…' : 'Approve'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
